import { isRecord, translateLocalized, useHandle, useI18n } from "../client";
import type { PageExtrasContext, PageHandle } from "../client";
import { BreadcrumbItem, Breadcrumbs as NextBreadcrumbs, type BreadcrumbsProps } from '@heroui/react';
import clsx from "clsx";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useParams } from "react-router";

export interface PageTitleProps {
    className?: string;
    isSticky?: boolean;
    isBlurred?: boolean;
    visible?: boolean;
    breadcrumbProps?: Omit<BreadcrumbsProps, "items">;
}

export const PageTitle: React.FC<PageTitleProps> = ({
    className,
    isBlurred = true,
    isSticky = true,
    visible = true,
    breadcrumbProps
}) => {

    const { matches, pageExtras, meta, pageMeta, handle } = useHandle();
    const { t } = useI18n();
    const location = useLocation();
    const params = useParams();

    // 获取配置了 breadcrumbs 的路由匹配项，并去除重复
    const breadcrumbItems = matches?.filter(match => 
        isRecord(match.handle) && match.handle.breadcrumbs !== undefined
    ).reduce((unique, match) => {
        // 检查是否已存在相同的路径名或标题
        const isDuplicate = unique.some(existingMatch => {
            if (existingMatch.pathname === match.pathname) return true;
            
            const existingHandle = existingMatch.handle as PageHandle;
            const currentHandle = match.handle as PageHandle;
            const existingTitle = translateLocalized(existingHandle.pageMeta?.title, t);
            const currentTitle = translateLocalized(currentHandle.pageMeta?.title, t);
            
            return existingTitle && currentTitle && existingTitle === currentTitle;
        });
        
        return isDuplicate ? unique : [...unique, match];
    }, [] as typeof matches) || [];

    return (matches && matches.length > 0) && (
        <>
            <Helmet>
                {<title>{translateLocalized(meta?.title, t)}</title>}
                {<meta name="description" content={translateLocalized(meta?.description, t) as string}></meta>}
            </Helmet>
            <div className={clsx(
                "flex justify-between gap-4 px-10 pb-4 pt-4",
                {
                    "sticky top-0 w-full z-40 backdrop-blur-sm": isSticky,
                    "backdrop-blur-lg": isBlurred && !isSticky,
                    "hidden": !visible
                },
                className
            )}>
                <div className="flex flex-col justify-center gap-4 transform-none">
                    {/* 只在有有效 breadcrumbs 配置时显示 */}
                    {breadcrumbItems.length > 0 && (
                        <NextBreadcrumbs variant="light" {...breadcrumbProps}>
                            {breadcrumbItems.map((match, index) => {
                                const { id, pathname, handle } = match;
                                const isCurrentPage = index === breadcrumbItems.length - 1;

                                if (isRecord(handle)) {
                                    // 使用用户配置的 breadcrumbs
                                    const { breadcrumbs, pageMeta: matchPageMeta } = handle as PageHandle;
                                    const title = translateLocalized(matchPageMeta?.title, t);
                                    
                                    return (
                                        <BreadcrumbItem
                                            key={id}
                                            classNames={{
                                                item: "flex text-small"
                                            }}
                                            {...breadcrumbs}
                                        >
                                            {isCurrentPage || !title ? (
                                                <span>{title}</span>
                                            ) : (
                                                <Link to={pathname}>{title}</Link>
                                            )}
                                        </BreadcrumbItem>
                                    );
                                }
                                return null;
                            })}
                        </NextBreadcrumbs>
                    )}
                    <div className="flex flex-col">
                        {pageMeta?.title && <h1 className="text-xl font-bold text-default-900 lg:text-2xl">{translateLocalized(pageMeta.title, t)}</h1>}
                        {pageMeta?.description && <p className="text-small text-default-400 lg:text-medium">{translateLocalized(pageMeta.description, t)}</p>}
                    </div>
                </div>
                {typeof pageExtras === 'function' ? pageExtras({
                    path: location.pathname,
                    params: params as Record<string, string>,
                    query: Object.fromEntries(new URLSearchParams(location.search)),
                    matches: matches || [],
                    handle: handle || {}
                } as PageExtrasContext) : pageExtras}
            </div>
        </>
    )
}